export const navLinks = [{
  label: 'Product',
  href: '#product'
}, {
  label: 'How it works',
  href: '#how'
}, {
  label: 'Evidence',
  href: '#evidence'
}, {
  label: 'Compliance',
  href: '#compliance'
}, {
  label: 'Company',
  href: '/company'
}];
export const heroCommitments = [{
  value: '60–90 min',
  label: 'Browser sandbox session'
}, {
  value: 'Six scenarios',
  label: 'One trap class, two entirely clean'
}, {
  value: 'Every release',
  label: 'Scenarios re-tested on new frontier models'
}, {
  value: 'Human sign-off',
  label: 'Required before any decision'
}];
export const forkStart = 'One candidate, one brief, an hour of work';
export const forkPaths = [{
  title: 'A take-home today',
  tone: 'dead' as const,
  nodes: ['Frontier model, full access to everything', 'Polished deliverable, indistinguishable from the rest', 'No hiring signal']
}, {
  title: 'The same hour with Lentic',
  tone: 'live' as const,
  nodes: ['Assistant that reasons, without the source documents', 'Six scenarios: one trap class, two clean', 'Evidence-backed hiring signal']
}];
export const comparisonRows = [{
  axis: 'Premise',
  detection: "Assumes AI isn't used",
  lentic: 'Assumes AI, and supplies it'
}, {
  axis: 'Durability',
  detection: 'Breaks on every model release',
  lentic: 'Re-tested on every model release'
}, {
  axis: 'Failure mode',
  detection: 'False positives, and the exposure they carry',
  lentic: "An inconclusive result, which we report as inconclusive"
}, {
  axis: 'Evidence',
  detection: 'Black-box similarity score',
  lentic: 'Every judgement traces to one action'
}, {
  axis: "When it's wrong",
  detection: "A candidate is rejected for something they didn't do",
  lentic: "A candidate's record shows nothing conclusive, and you interview them"
}, {
  axis: 'Posture',
  detection: 'Surveillance',
  lentic: 'Assessment'
}];
export const atsBlocks = [{
  title: 'Structural conflict',
  body: 'A workflow platform that screens, ranks and recommends cannot also be the evidentiary layer proving those steps were sound. It is the same conflict that separated statutory audit from bookkeeping. As applicant tracking systems embed more AI, they become the subject of the audit rather than the auditor, and that gap widens with every feature they ship.'
}, {
  title: 'Evidentiary weight',
  body: 'A record held by an independent party with no stake in the outcome, under a versioned and hashed methodology, answers a question an internal system cannot: could this have been assembled after the fact to support the decision?'
}, {
  title: "Self-certification is ending",
  body: 'NYC Local Law 144 requires the bias audit of an automated employment decision tool to be conducted by an independent auditor, explicitly excluding parties with a financial interest. The EU AI Act separates provider obligations from deployer obligations. Self-certification is not the direction of travel.'
}];
export const scenarioClasses = [{
  name: 'AI-plausible error',
  count: "LATENT ERROR",
  what: 'The evidence structure leads the assistant to a confident, subtly incorrect conclusion.',
  reveals: 'Whether the candidate verifies before relying on an answer that looks right.'
}, {
  name: 'Source ambiguity',
  count: 'Ambiguous',
  what: 'Two documents appear to conflict; a legitimate explanation exists but is not stated.',
  reveals: 'Whether the conflict is noticed, investigated, then resolved, qualified or escalated.'
}, {
  name: 'Multi-step relational',
  count: "DISTRIBUTED",
  what: 'No single document is wrong; the conclusion requires chaining evidence across several.',
  reveals: 'Decomposition and independent reasoning.'
}, {
  name: 'Clean case',
  count: 'Clean',
  clean: true,
  what: 'The assistant is correct and the evidence supports it cleanly.',
  reveals: 'Whether the candidate verifies proportionately and moves on, rather than manufacturing a problem.'
}];
export const sessionSteps = [{
  time: '3 min',
  title: 'Consent',
  body: "Full disclosure before anything is recorded, including the fact that a human reviews the record before any decision is made."
}, {
  time: '5 min',
  title: 'Briefing',
  body: 'Role-realistic scenario and source files. Use the AI as much as you want.'
}, {
  time: '45–75 min',
  title: 'Working session',
  body: 'The candidate works the brief. Decisions and evidence transitions are recorded.'
}, {
  time: '10 min',
  title: 'Reflection',
  body: 'What did you check, what did you take on trust, what would another hour buy.'
}];
export type TraceKind = 'prompt' | 'ai' | 'file' | 'defect' | 'flag' | 'clean';
export const traceEvents: {
  time: string;
  kind: TraceKind;
  label: string;
  detail: string;
  evidence: string;
  weight: 'high' | 'low';
}[] = [{
  time: '00:04:20',
  kind: 'file',
  label: 'Opened brief.pdf',
  detail: 'Read the full brief before prompting, with a second pass over the constraints in §2.',
  evidence: 'dwell 3m 41s · 2 passes',
  weight: 'low'
}, {
  time: '00:12:04',
  kind: 'prompt',
  label: 'Prompt · reconcile intercompany balances',
  detail: 'Task handed over whole rather than staged. No source figures supplied with the request, so the assistant had nothing to reconcile against.',
  evidence: 'prompt 1 of 14 · 62 words',
  weight: 'low'
}, {
  time: '00:14:31',
  kind: 'ai',
  label: 'AI conclusion accepted unedited',
  detail: '"Intercompany balances agree; no adjustment required." Pasted into the deliverable with no edit and no source consulted. Reconstructed in full in the decision record.',
  evidence: '0 edits · no source consulted',
  weight: 'high'
}, {
  time: '00:39:58',
  kind: 'file',
  label: 'Opened brief.pdf · returned to §4',
  detail: 'Went back to the stated constraints after an answer looked too clean. First verification-led action of the session.',
  evidence: 'return visit · §4',
  weight: 'low'
}, {
  time: '00:45:50',
  kind: 'defect',
  label: 'Source ambiguity resolved',
  detail: 'Divergent values across two supporting documents identified, and the unstated authority hierarchy escalated in the deliverable rather than resolved by guessing.',
  evidence: 'scenario 3 of 6 · full credit',
  weight: 'high'
}, {
  time: '00:58:02',
  kind: 'clean',
  label: 'Clean case verified proportionately',
  detail: 'Checked the assistant against one supporting document, found it correct, and moved on without manufacturing a problem. Over-checking is scored here, not rewarded.',
  evidence: 'scenario 4 of 6 · clean · full credit',
  weight: 'high'
}, {
  time: '01:02:16',
  kind: 'flag',
  label: 'AI-plausible error appropriately escalated',
  detail: 'Queried the translation date, rejected the first response, and recorded residual uncertainty. In most roles this is the correct professional behaviour, so it earns near-full credit.',
  evidence: 'scenario 5 of 6 · near-full credit',
  weight: 'high'
}];
export const reconstruction = [{
  field: 'Assistant concluded',
  value: '"Intercompany balances agree; no adjustment required."'
}, {
  field: 'Evidence available',
  value: 'schedule_b.xlsx · brief §4 · AB ledger extract'
}, {
  field: 'Source consulted',
  value: 'None'
}, {
  field: 'Candidate action',
  value: 'Accepted, unedited'
}, {
  field: 'What the source would have established',
  value: 'Counterparty reversed in supporting schedule'
}, {
  field: 'Assistant was',
  value: 'Incorrect'
}, {
  field: 'Outcome',
  value: 'Missed'
}];

/** Recorded at submission, after the work — kept separate from the chronology. */
export const reconstructionAttestation = [{
  field: 'Candidate confidence',
  value: 'High'
}, {
  field: 'Would sign off?',
  value: 'Yes',
  adverse: true
}];
export const pipelineStages = [{
  stage: 'Stage 1',
  title: 'Synthetic scaffolding',
  engine: 'LLM',
  body: 'Entity, narrative, ledger, budget and supporting documents, generated to a schema.'
}, {
  stage: 'Stage 2',
  title: 'Deterministic construction',
  engine: 'Python',
  body: 'Seeded, reproducible scenario builds that emit a machine-readable ground truth record.'
}, {
  stage: 'Stage 3',
  title: 'Adversarial gate',
  engine: 'Frontier APIs',
  body: 'Submitted cold to current models. Anything resolved unprompted fails and regenerates.'
}, {
  stage: 'Stage 4',
  title: 'Human validation',
  engine: 'Practitioner',
  body: 'Pass/fail review that the surviving scenarios are realistic, not generator artefacts.'
}];
export const gateFacts = [{
  value: 'Weeks',
  label: 'Realistic scenario shelf life, not years'
}, {
  value: 'Auto',
  label: 'Deprecation when a scenario stops holding'
}, {
  value: 'Locked',
  label: 'Library version pinned for the life of a requisition'
}];
export const modelIndependence = [{
  label: 'Scenario construction',
  value: 'No model dependency'
}, {
  label: 'Ground truth records',
  value: 'No model dependency'
}, {
  label: 'Measurement layer',
  value: 'No model dependency'
}, {
  label: 'Record schema and retention',
  value: 'No model dependency'
}];
export const archetypes = [{
  id: 'counterparty_mismatch',
  behaviour: 'Amount reconciles; entity swapped for a plausible sibling.'
}, {
  id: 'silent_restatement',
  behaviour: 'Prior-period comparative altered, with no restatement note.'
}, {
  id: 'source_conflict',
  behaviour: 'Two documents disagree; authority hierarchy left unstated.'
}, {
  id: 'stale_period',
  behaviour: "A document's period shifted a quarter, metadata held consistent."
}, {
  id: 'internal_contradiction',
  behaviour: 'A constraint in brief §2 quietly negated in §6.'
}, {
  id: 'classification_drift',
  behaviour: 'Correct total, adjacent account, altered headline ratio.'
}, {
  id: 'fx_timing',
  behaviour: 'Correct rate, wrong date, on a European entity.'
}];
export const compliancePoints = [{
  tag: 'EU AI Act',
  body: 'Designed against the high-risk obligations. The library manifest and ground truth records produce the technical logging as a by-product of the architecture.'
}, {
  tag: 'GDPR Art. 22',
  body: 'Supports a DPIA, meaningful information about the logic, and a human review that is substantive rather than nominal.'
}, {
  tag: 'Data minimisation',
  body: 'Structured decision events instead of persisted video. Ephemeral streams, defined retention, and every captured field disclosed specifically.'
}, {
  tag: 'NYC LL144',
  body: 'Built to be audited by an independent auditor: versioned methodology, candidate notice, and adverse impact monitoring by protected characteristic.'
}, {
  tag: 'No attention tracking',
  body: 'We capture decisions and evidence transitions, not continuous observation. Every retained field answers a specific hiring question and carries a written justification.'
}];
export const neverCaptured = ['No video', 'No webcam', 'No keystroke logging outside the workspace', 'No personality or trait inference', 'No affect analysis', 'No demographic inference'];
export const candidateFacts = ['Use the AI as much as you like. We measure how, not whether.', 'Full disclosure of what is captured, before the session starts.', '60–90 minutes, asynchronous, no webcam, no proctor.', 'Accommodations on request: extended timing, reduced-telemetry mode, alternative formats.', 'A written account of their own performance, available on request.', 'No pass mark is published to candidates. The record is evidence, not a grade.'];
export const evidenceEstablished = ['The mechanic runs end to end in a live sandbox', 'Scenarios survive cold testing against three frontier models under four prompting conditions', 'Every score decomposes to one action, one timestamp, one scenario'];
export const evidenceInProgress = ['A 20–30 participant study against independently rated professional quality, pre-registered, with results published including failures', 'A three-arm comparison: assistant alone, human with full-access assistant, and Lentic', 'Predictive validity against job outcomes, a 12–18 month question we are not yet claiming'];
export const decisionSummaryException = 'One material verification failure: an incorrect AI conclusion was accepted without opening the supporting schedule, and the candidate recorded that they would have signed off on it.';

export const decisionSummary = 'Calibrated judgement was observed across the other five scenarios. The candidate challenged one plausible AI error, resolved a source ambiguity by escalating the unstated authority question rather than guessing, and accepted both clean cases after proportionate verification.';

export const defectResults = [{
  id: 'Scenario 1',
  type: 'Clean case',
  outcome: 'Verified proportionately' as const,
  detail: 'Single confirming check against the ledger extract, then moved on.',
  credit: 'Full'
}, {
  id: 'Scenario 2',
  type: 'Multi-step relational',
  outcome: 'Handled · resolved correctly' as const,
  detail: 'Chained the budget, ledger and schedule to reach the conclusion unaided by the assistant.',
  credit: 'Full'
}, {
  id: 'Scenario 3',
  type: 'Source ambiguity',
  outcome: 'Handled · resolved correctly' as const,
  detail: 'Both schedules opened at 00:41:12, divergence investigated, authority question escalated.',
  credit: 'Full'
}, {
  id: 'Scenario 4',
  type: 'Clean case',
  outcome: 'Verified proportionately' as const,
  detail: 'Assistant correct and supported by the evidence. Checked once, accepted, no manufactured problem.',
  credit: 'Full'
}, {
  id: 'Scenario 5',
  type: 'AI-plausible error',
  outcome: 'Handled · appropriately escalated' as const,
  detail: 'Translation date queried, first response rejected, the open question escalated in the deliverable.',
  credit: 'Near-full'
}, {
  id: 'Scenario 6',
  type: 'AI-plausible error',
  outcome: 'Missed' as const,
  detail: 'Conclusion accepted from the assistant; the supporting schedule was never opened.',
  credit: 'None',
  reconstructed: true
}];

export const observedPrimary = {
  label: 'Source consulted before relying on AI output',
  value: '7 of 9 material decisions'
};

export const observedSupporting = [{
  label: 'Clean cases appropriately accepted',
  value: '2 of 2'
}, {
  label: 'AI output challenged where required',
  value: '1 of 2 material AI errors'
}, {
  label: 'AI output materially edited or discarded',
  value: '46%'
}, {
  label: 'Prompts, staged with checkpoints',
  value: '14'
}];

export const candidateReflection = 'I checked both schedules on the intercompany question because the totals felt too clean. On the translation date I ran out of time and flagged it rather than guessing. Looking back I should have opened schedule_b before accepting the first reconciliation.';
export const companyIntro = ['Lentic is an independent assessment layer for hiring into AI-assisted work.', 'We put candidates in a sandbox with an assistant that can reason but cannot see the source documents, and we record how they handle six scenarios of known construction.', 'What the client buys is the record that proves how the decision was made, held by a party with no stake in the outcome.'];
export const founders = [{
  name: 'Founder name',
  role: 'Co-founder',
  background: 'Fifteen years in group finance and controls, latterly leading month-end close for a listed European group.',
  linkedin: 'https://www.linkedin.com'
}, {
  name: 'Founder name',
  role: 'Co-founder',
  background: 'Machine learning engineer; previously built evaluation and measurement infrastructure for production model systems.',
  linkedin: 'https://www.linkedin.com'
}];
export const footerColumns = [{
  title: 'Product',
  links: [{
    label: 'How it works',
    href: '#how'
  }, {
    label: 'Session trace',
    href: '#trace'
  }, {
    label: 'Decision record',
    href: '#record'
  }, {
    label: 'Scenario library',
    href: '#library'
  }]
}, {
  title: 'Company',
  links: [{
    label: 'About Lentic',
    href: '/company'
  }, {
    label: 'Evidence and status',
    href: '#evidence'
  }, {
    label: 'Compliance',
    href: '#compliance'
  }, {
    label: 'Pilot pricing',
    href: '#contact'
  }]
}, {
  title: 'Legal',
  links: [{
    label: 'Privacy policy',
    href: '#legal'
  }, {
    label: 'Candidate privacy notice',
    href: '#legal'
  }, {
    label: 'Data processing agreement',
    href: '#legal'
  }, {
    label: 'Security',
    href: '#legal'
  }]
}];